import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useUnreadCount from '../../hooks/useUnreadCount';
import { cn } from '../../lib/utils';
import { Icon } from '../common/Icon';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { useSiteSettings } from '../../context/SiteSettingsContext';

/**
 * Role-scoped side navigation. This component renders ONLY the nav column;
 * the global logo/branding is rendered by Header (dashboard routes) or
 * AdminLayout (admin routes), never here.
 *
 * The header offset is read from the CSS custom property `--header-h` and
 * falls back to 4rem (the current Header height). Any future change to the
 * Header height only needs to update that variable — no fragile hard-coded
 * `top-16` values here.
 */

const customerNavItems = [
  { icon: 'navigation.dashboard', labelKey: 'sidebar.dashboard', href: '/dashboard' },
  { icon: 'file-text', labelKey: 'sidebar.myRequests', href: '/my-requests' },
  { icon: 'package-check', labelKey: 'sidebar.partsPurchaseHistory', href: '/parts-purchase-history' },
  { icon: 'vehicles.car', labelKey: 'sidebar.myVehicles', href: '/vehicles' },
  { icon: 'sparkles', labelKey: 'sidebar.vehicleCare', href: '/vehicle-care' },
  { icon: 'support.message', labelKey: 'sidebar.messages', href: '/chats' },
  { icon: 'bookmark', labelKey: 'sidebar.savedSearches', href: '/saved-searches' },
  { icon: 'profile.user', labelKey: 'sidebar.profile', href: '/profile' },
  { icon: 'sparkles', labelKey: 'sidebar.aiHelp', href: '/support' },
  { icon: 'life-buoy', labelKey: 'sidebar.supportTickets', href: '/support/tickets' },
];

const garageNavItems = [
  { icon: 'navigation.dashboard', labelKey: 'sidebar.dashboard', href: '/garage/dashboard' },
  // UAT-1: provider Incoming Repairs is the provider-CRM "Leads" view.
  // Old blank `/garage/leads` page retired; clicks now go to /garage/incoming.
  { icon: 'inbox', labelKey: 'sidebar.leads', href: '/garage/incoming' },
  { icon: 'clipboard-check', labelKey: 'sidebar.garageJobs', href: '/garage/jobs' },
  { icon: 'package', labelKey: 'sidebar.sourceParts', href: '/garage/source-parts' },
  { icon: 'file-text', labelKey: 'sidebar.myRfqs', href: '/garage/rfqs' },
  { icon: 'shield-check', labelKey: 'sidebar.kycVerification', href: '/garage/kyc' },
  { icon: 'billing.payment', labelKey: 'sidebar.myPlan', href: '/garage/my-plan' },
  { icon: 'bar-chart-3', labelKey: 'sidebar.myUsage', href: '/garage/my-usage' },
  { icon: 'billing.invoice', labelKey: 'sidebar.billing', href: '/garage/billing' },
  { icon: 'wrench', labelKey: 'sidebar.businessProfile', href: '/garage/profile' },
  { icon: 'profile.user', labelKey: 'sidebar.accountSettings', href: '/garage/account' },
  { icon: 'support.message', labelKey: 'sidebar.messages', href: '/garage/chats' },
  { icon: 'bookmark', labelKey: 'sidebar.savedSearches', href: '/garage/saved-searches' },
  { icon: 'sparkles', labelKey: 'sidebar.aiHelp', href: '/garage/support' },
  { icon: 'life-buoy', labelKey: 'sidebar.supportTickets', href: '/garage/support/tickets' },
];

const dealerNavItems = [
  { icon: 'navigation.dashboard', labelKey: 'sidebar.dashboard', href: '/dealer/dashboard' },
  // UAT-1: dealer Incoming Requests is the provider-CRM "Leads" view.
  // Old blank `/dealer/leads` page retired; clicks now go to /dealer/requests.
  { icon: 'inbox', labelKey: 'sidebar.leads', href: '/dealer/requests' },
  { icon: 'package', labelKey: 'sidebar.sourceParts', href: '/dealer/source-parts' },
  { icon: 'file-text', labelKey: 'sidebar.myRfqs', href: '/dealer/rfqs' },
  { icon: 'shield-check', labelKey: 'sidebar.kycVerification', href: '/dealer/kyc' },
  { icon: 'billing.payment', labelKey: 'sidebar.myPlan', href: '/dealer/my-plan' },
  { icon: 'bar-chart-3', labelKey: 'sidebar.myUsage', href: '/dealer/my-usage' },
  { icon: 'billing.invoice', labelKey: 'sidebar.billing', href: '/dealer/billing' },
  { icon: 'wrench', labelKey: 'sidebar.businessProfile', href: '/dealer/profile' },
  { icon: 'profile.user', labelKey: 'sidebar.accountSettings', href: '/dealer/account' },
  { icon: 'support.message', labelKey: 'sidebar.messages', href: '/dealer/chats' },
  { icon: 'bookmark', labelKey: 'sidebar.savedSearches', href: '/dealer/saved-searches' },
  { icon: 'sparkles', labelKey: 'sidebar.aiHelp', href: '/dealer/support' },
  { icon: 'life-buoy', labelKey: 'sidebar.supportTickets', href: '/dealer/support/tickets' },
];

const adminNavItems = [
  { icon: 'navigation.dashboard', label: 'Dashboard', href: '/admin' },
  { icon: 'admin.users', label: 'Providers', href: '/admin/providers' },
  { icon: 'check-square', label: 'Verification', href: '/admin/verification' },
  { icon: 'globe', label: 'Currencies', href: '/admin/currencies' },
  { icon: 'file-text', label: 'Requests', href: '/admin/requests' },
  { icon: 'clipboard-list', label: 'Master Data', href: '/admin/master-data' },
  { icon: 'bar-chart-3', label: 'Audit Logs', href: '/admin/audit-logs' },
  { icon: 'package', label: 'Vehicle Ownership', href: '/admin/vehicle-ownership' },
  { icon: 'navigation.settings', label: 'System Settings', href: '/admin/system-settings' },
  { icon: 'book-open', label: 'User Manuals', href: '/admin/docs' },
];

export function Sidebar({ open, onClose }) {
  const { t } = useTranslation('shared');
  const { isCustomer, isGarage, isAdmin, user } = useAuth();
  const location = useLocation();
  const isLoggedIn = !!user;
  const { unreadCount } = useUnreadCount(isLoggedIn && !isAdmin);
  const { capabilities } = useSiteSettings();

  const isDealer = user?.role === 'dealer_owner';

  const roleNavItems = isAdmin
    ? adminNavItems
    : isDealer
      ? dealerNavItems
      : isGarage
        ? garageNavItems
        : customerNavItems;
  const navItems = roleNavItems.filter((item) => {
    if (!capabilities.job_management_enabled && item.href === '/garage/jobs') return false;
    if (!capabilities.transactional_parts_enabled && item.href === '/parts-purchase-history') return false;
    return true;
  });

  // Determine which href is the messages route for this role
  const messagesHref = isDealer
    ? '/dealer/chats'
    : isGarage
      ? '/garage/chats'
      : '/chats';

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'auth-sidebar fixed start-0 z-40 w-64 border-e border-white/10 bg-[#071b40] text-white transition-transform duration-200 ease-in-out md:translate-x-0',
          'top-[var(--header-h,4rem)] h-[calc(100vh-var(--header-h,4rem))]',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between p-4 md:hidden">
          <span className="font-heading font-semibold text-white">{t('workspace.title')}</span>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="sidebar-close">
            <Icon name="navigation.close" className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="h-full py-3">
          <div className="px-4 pb-4 pt-2"><p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45">{isDealer ? t('workspace.dealer') : isGarage ? t('workspace.garage') : isCustomer ? t('workspace.vehicleOwner') : t('workspace.title')}</p></div>
          <nav className="px-3 space-y-1">
            {navItems.map((item) => {
              const isMessages = item.href === messagesHref;
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'ds-nav-link flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                      isActive || location.pathname.startsWith(item.href)
                        ? 'ds-nav-active bg-white text-[#071b40] shadow-sm'
                        : 'text-white/72 hover:bg-white/10 hover:text-white',
                    )
                  }
                  data-testid={`sidebar-${(item.labelKey || item.label || 'item').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{item.labelKey ? t(item.labelKey) : item.label}</span>
                  {isMessages && unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[10px] font-bold"
                      data-testid="sidebar-unread-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>
      </aside>
    </>
  );
}

export default Sidebar;
